import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { SkupremeModal, LoaderBar } from '../../../components';
import {
  HoverBorderButton,
  StyledButton,
  MuteText,
  BoldText,
  StyledCheckbox,
} from '../../../elements';
import { carrierImageMap, CommonUtility } from '../../../utility';

// ========== Styled Components ==========
const LabelItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.background};
  border-radius: 5px;
  margin-bottom: 8px;
  background: ${({ theme }) => theme.colors.white};
`;

const LabelInfo = styled.div`
  flex: 1;
  margin-left: 12px;
`;

const LabelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const SelectAllContainer = styled.div`
  padding: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.background};
  margin-bottom: 12px;
`;

const LabelsScrollContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const CarrierIcon = styled.img`
  width: 24px;
  height: 24px;
`;

// ========== TypeScript Interfaces ==========
interface ILabel {
  _id?: string;
  id?: string;
  carrier_code?: string;
  carrier?: string;
  tracking_id?: string;
  tracking?: string;
  service?: {
    value?: string;
    key?: string;
  };
}

interface ILabelSelectionPopupProps {
  open: boolean;
  onClose: () => void;
  labels?: ILabel[];
  onConfirm: (labelIds: string[]) => void;
  loading?: boolean;
}

// ========== Component ==========
export function LabelSelectionPopup({
  open,
  onClose,
  labels = [],
  onConfirm,
  loading = false,
}: ILabelSelectionPopupProps) {
  const [selectedLabels, setSelectedLabels] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open && labels.length > 0) {
      // Select all labels by default
      setSelectedLabels(new Set(labels.map(label => label._id || label.id || '')));
    }
  }, [open, labels]);

  const handleToggleLabel = (labelId: string) => {
    const newSelected = new Set(selectedLabels);
    if (newSelected.has(labelId)) {
      newSelected.delete(labelId);
    } else {
      newSelected.add(labelId);
    }
    setSelectedLabels(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedLabels.size === labels.length) {
      setSelectedLabels(new Set());
    } else {
      setSelectedLabels(new Set(labels.map(label => label._id || label.id || '')));
    }
  };

  const handleConfirm = () => {
    if (selectedLabels.size === 0) {
      return;
    }
    onConfirm(Array.from(selectedLabels));
  };

  return (
    <SkupremeModal
      open={open}
      onClose={loading ? () => {} : onClose}
      title="Select Labels to Regenerate"
      size="small"
    >
      {loading && <LoaderBar />}
      <div className="mb-4">
        <MuteText>
          Select which labels you want to regenerate. The selected labels will be refunded
          and new labels will be created with the updated address.
        </MuteText>
      </div>

      {labels.length > 1 && (
        <SelectAllContainer>
          <StyledCheckbox
            checked={selectedLabels.size === labels.length}
            onChange={handleSelectAll}
            label="Select All"
            disabled={loading}
          />
        </SelectAllContainer>
      )}

      <LabelsScrollContainer>
        {labels.map(label => {
          const labelId = label._id || label.id || '';
          const isSelected = selectedLabels.has(labelId);
          const carrierCode = label.carrier_code || label.carrier || 'unknown';

          return (
            <LabelItem key={labelId}>
              <StyledCheckbox
                checked={isSelected}
                onChange={() => handleToggleLabel(labelId)}
                disabled={loading}
              />
              <LabelInfo>
                <LabelHeader>
                  {carrierImageMap[carrierCode] ? (
                    <CarrierIcon src={carrierImageMap[carrierCode]} alt={carrierCode} />
                  ) : null}
                  <BoldText>
                    {CommonUtility.removeUnderscoreAndCapitalize(carrierCode)}
                  </BoldText>
                  {label.service?.value && (
                    <MuteText>
                      - {CommonUtility.removeUnderscoreAndCapitalize(label.service.value)}
                    </MuteText>
                  )}
                </LabelHeader>
                <MuteText>
                  Tracking: {label.tracking_id || label.tracking || 'N/A'}
                </MuteText>
              </LabelInfo>
            </LabelItem>
          );
        })}
      </LabelsScrollContainer>

      <div className="d-flex justify-content-end mt-4">
        <HoverBorderButton
          onClick={onClose}
          disabled={loading}
          type="button"
          className="mr-2"
        >
          Cancel
        </HoverBorderButton>
        <StyledButton
          loading={loading}
          onClick={handleConfirm}
          disabled={selectedLabels.size === 0}
        >
          Confirm ({selectedLabels.size} selected)
        </StyledButton>
      </div>
    </SkupremeModal>
  );
}
